/**
 * Web Firestore Adapter with Comprehensive Logging
 * Direct wrapper around Firebase Web SDK - assumes Firebase is pre-initialized in index.web.js
 * Every operation is logged with detailed debugging information
 */

// IMMEDIATE LOG - should appear when module loads
// Add a small structured logger to produce consistent, timestamped output
function _safeStringify(obj: any) {
  try {
    return JSON.stringify(obj, (_key, value) => {
      // avoid dumping large Firebase internals; truncate functions
      if (typeof value === 'function') return '[Function]';
      return value;
    }, 2);
  } catch (e) {
    try { return String(obj); } catch (e2) { return '[unstringifiable]'; }
  }
}

function LOG(tag: string, msg: string, data?: any) {
  const ts = new Date().toISOString();
  if (data === undefined) {
    console.log(`${ts} 🔥 [${tag}] ${msg}`);
  } else {
    console.log(`${ts} 🔥 [${tag}] ${msg}`, data instanceof Error ? {message: data.message, stack: data.stack} : data, _safeStringify(data));
  }
}

LOG('FIRESTORE-STUB', 'firestoreStub.ts MODULE IS LOADING');

// Mock classes for fallback when Firebase is not available
class DocumentSnapshot {
  constructor(private _id: string, private _data: any, private _exists: boolean) {}
  get id() { return this._id; }
  get exists() { return this._exists; }
  data() { 
    const data = this._data || {};
    console.log(`🔥 [FIRESTORE-STUB] DocumentSnapshot.data()`, data);
    return data;
  }
  get(field: string) {
    const value = this._data?.[field];
    console.log(`🔥 [FIRESTORE-STUB] DocumentSnapshot.get('${field}')`, value);
    return value;
  }
}

class QuerySnapshot {
  constructor(private _docs: DocumentSnapshot[]) {
    console.log(`🔥 [FIRESTORE-STUB] QuerySnapshot created with ${_docs.length} docs`);
  }
  get docs() { 
    console.log(`🔥 [FIRESTORE-STUB] QuerySnapshot.docs accessed: ${this._docs.length} docs`);
    return this._docs;
  }
  get size() { 
    console.log(`🔥 [FIRESTORE-STUB] QuerySnapshot.size: ${this._docs.length}`);
    return this._docs.length;
  }
  forEach(callback: (doc: DocumentSnapshot) => void) { 
    console.log(`🔥 [FIRESTORE-STUB] QuerySnapshot.forEach called`);
    this._docs.forEach(callback);
  }
  empty() { 
    const isEmpty = this._docs.length === 0;
    console.log(`🔥 [FIRESTORE-STUB] QuerySnapshot.empty(): ${isEmpty}`);
    return isEmpty;
  }
}

const firestoreStub = {
  collection: (name: string) => {
    console.log(`🔥 [FIRESTORE-STUB] collection('${name}') - returning stub`);
    return {
      where: () => {
        console.log(`🔥 [FIRESTORE-STUB] where() - returning stub`);
        return {
          onSnapshot: () => {
            console.log(`🔥 [FIRESTORE-STUB] onSnapshot() - returning empty stub listener`);
            return () => {};
          }
        };
      }
    };
  },
  doc: (ref: string) => {
    console.log(`🔥 [FIRESTORE-STUB] doc('${ref}') - returning stub`);
    return {
      onSnapshot: () => {
        console.log(`🔥 [FIRESTORE-STUB] doc.onSnapshot() - returning empty stub listener`);
        return () => {};
      }
    };
  },
  batch: () => {
    console.log(`🔥 [FIRESTORE-STUB] batch() - returning stub`);
    return {
      set: () => console.log(`🔥 [FIRESTORE-STUB] batch.set() - stub`),
      update: () => console.log(`🔥 [FIRESTORE-STUB] batch.update() - stub`),
      delete: () => console.log(`🔥 [FIRESTORE-STUB] batch.delete() - stub`),
      commit: () => {
        console.log(`🔥 [FIRESTORE-STUB] batch.commit() - returning resolved stub`);
        return Promise.resolve();
      }
    };
  },
};

let cachedRealFirestore: any = null;

LOG('FIRESTORE', 'Module loaded - waiting for firestore() call');

export default function firestore() {
  LOG('FIRESTORE', '=== firestore() FUNCTION CALLED ===');
  try {
    const firebaseApp = require('firebase/app');
    const firebaseModule = require('firebase/firestore');
    LOG('FIRESTORE', 'Firebase modules loaded successfully');
    
    const apps = firebaseApp.getApps?.();
    const appsCount = apps?.length || 0;
    console.log(`🔥 [FIRESTORE] Checking Firebase initialization: ${appsCount} apps found`);
    
    if (!apps || apps.length === 0) {
      console.log('⚠️  [FIRESTORE] ❌ Firebase NOT initialized - returning STUB');
      return firestoreStub;
    }
    
    if (cachedRealFirestore) {
      console.log('✅ [FIRESTORE] Using cached real Firestore wrapper');
      return cachedRealFirestore;
    }
    
    LOG('FIRESTORE', 'Firebase IS initialized - creating REAL wrapper');
    const db = firebaseModule.getFirestore();
    LOG('FIRESTORE', 'Firestore instance obtained');
    
    // Validate that we can actually use the firestore instance
    if (!db) {
      console.log('⚠️  [FIRESTORE] Firestore instance is null, using stub');
      return firestoreStub;
    }
    
    const createQueryBuilder = (collRef: any, depth: number = 0) => {
      const indent = '  '.repeat(depth);
      return {
        where: (field: string, op: string, value: any) => {
          console.log(`🔥 [FIRESTORE]${indent} .where('${field}' '${op}' ${JSON.stringify(value).substring(0, 50)}...)`);
          const constraint = firebaseModule.where(field, op, value);
          const q = firebaseModule.query(collRef, constraint);
          return {
            where: (f: string, o: string, v: any) => {
              console.log(`🔥 [FIRESTORE]${indent} .where() chained`);
              return createQueryBuilder(q, depth + 1).where(f, o, v);
            },
              onSnapshot: (callback: any, error?: any) => {
                LOG('FIRESTORE', `${indent}.onSnapshot() ATTACHED (where query)`);
                return firebaseModule.onSnapshot(q, (snap: any) => {
                  const count = snap.docs?.length || 0;
                  // build a lightweight docs summary (id + truncated data)
                  const docsSummary = (snap.docs || []).map((doc: any, i: number) => {
                    let dataPreview: any = null;
                    try {
                      // prefer doc.data() if available
                      dataPreview = typeof doc.data === 'function' ? doc.data() : doc;
                    } catch (e) {
                      dataPreview = '[unreadable]';
                    }
                    // avoid huge payloads: only include keys and small values
                    const small = {} as any;
                    if (dataPreview && typeof dataPreview === 'object') {
                      Object.keys(dataPreview).slice(0, 10).forEach(k => {
                        const v = dataPreview[k];
                        if (typeof v === 'string' && v.length > 200) small[k] = v.slice(0, 200) + '...';
                        else small[k] = v;
                      });
                    } else {
                      small.value = dataPreview;
                    }
                    return { id: doc.id, data: small };
                  });

                  LOG('FIRESTORE', `${indent}📡 onSnapshot FIRED: ${count} docs`, { count, docs: docsSummary });
                  if (count === 0) {
                    LOG('FIRESTORE', `${indent}⚠️  No documents matched the query`);
                  }
                  try {
                    callback(snap);
                  } catch (cbErr) {
                    LOG('FIRESTORE', `${indent} callback threw error`, cbErr);
                    throw cbErr;
                  }
                }, (err: any) => {
                  console.error(`🔥 [FIRESTORE]${indent} ❌ onSnapshot ERROR:`, err);
                  error?.(err);
                });
              },
            get: () => {
              console.log(`🔥 [FIRESTORE]${indent} .get() called (where query)`);
              return firebaseModule.getDocs(q).then((snap: any) => {
                const count = snap.docs?.length || 0;
                console.log(`🔥 [FIRESTORE]${indent} ✅ get() returned ${count} docs`);
                snap.docs.forEach((doc: any, i: number) => {
                  console.log(`🔥 [FIRESTORE]${indent}   [${i}] ID: ${doc.id}`, doc.data());
                });
                return snap;
              }).catch((err: any) => {
                console.error(`🔥 [FIRESTORE]${indent} ❌ get() ERROR:`, err);
                throw err;
              });
            },
          };
        },
        onSnapshot: (callback: any, error?: any) => {
          console.log(`🔥 [FIRESTORE]${indent} .onSnapshot() ATTACHED (full collection)`);
          return firebaseModule.onSnapshot(collRef, (snap: any) => {
            const count = snap.docs?.length || 0;
            console.log(`🔥 [FIRESTORE]${indent} 📡 Collection onSnapshot FIRED: ${count} docs`);
            snap.docs.forEach((doc: any, i: number) => {
              console.log(`🔥 [FIRESTORE]${indent}   [${i}] ID: ${doc.id}`, doc.data());
            });
            callback(snap);
          }, (err: any) => {
            console.error(`🔥 [FIRESTORE]${indent} ❌ onSnapshot ERROR:`, err);
            error?.(err);
          });
        },
        add: (data: any) => {
          console.log(`🔥 [FIRESTORE]${indent} .add() called`, data);
          return firebaseModule.addDoc(collRef, data).then((ref: any) => {
            console.log(`🔥 [FIRESTORE]${indent} ✅ add() SUCCESS - new ID: ${ref.id}`);
            return ref;
          }).catch((err: any) => {
            console.error(`🔥 [FIRESTORE]${indent} ❌ add() ERROR:`, err);
            throw err;
          });
        },
        get: () => {
          console.log(`🔥 [FIRESTORE]${indent} .get() called (full collection)`);
          return firebaseModule.getDocs(collRef).then((snap: any) => {
            const count = snap.docs?.length || 0;
            console.log(`🔥 [FIRESTORE]${indent} ✅ get() returned ${count} docs`);
            snap.docs.forEach((doc: any, i: number) => {
              console.log(`🔥 [FIRESTORE]${indent}   [${i}] ID: ${doc.id}`, doc.data());
            });
            return snap;
          }).catch((err: any) => {
            console.error(`🔥 [FIRESTORE]${indent} ❌ get() ERROR:`, err);
            throw err;
          });
        },
      };
    };
    
    cachedRealFirestore = {
      collection: (name: string) => {
        console.log(`🔥 [FIRESTORE] 📂 .collection('${name}')`);
        const collRef = firebaseModule.collection(db, name);
        return createQueryBuilder(collRef);
      },
      doc: (path: string) => {
        console.log(`🔥 [FIRESTORE] 📄 .doc('${path}')`);
        const docRef = firebaseModule.doc(db, path);
        return {
          get: () => {
            console.log(`🔥 [FIRESTORE] .get()`);
            return firebaseModule.getDoc(docRef).then((snap: any) => {
              if (snap.exists()) {
                console.log(`🔥 [FIRESTORE] ✅ get() found doc`, snap.data());
              } else {
                console.log(`🔥 [FIRESTORE] ⚠️  get() doc does not exist`);
              }
              return snap;
            }).catch((err: any) => {
              console.error(`🔥 [FIRESTORE] ❌ get() ERROR:`, err);
              throw err;
            });
          },
          set: (data: any) => {
            console.log(`🔥 [FIRESTORE] .set()`, data);
            return firebaseModule.setDoc(docRef, data).then(() => {
              console.log(`🔥 [FIRESTORE] ✅ set() SUCCESS`);
            }).catch((err: any) => {
              console.error(`🔥 [FIRESTORE] ❌ set() ERROR:`, err);
              throw err;
            });
          },
          update: (data: any) => {
            console.log(`🔥 [FIRESTORE] .update()`, data);
            return firebaseModule.updateDoc(docRef, data).then(() => {
              console.log(`🔥 [FIRESTORE] ✅ update() SUCCESS`);
            }).catch((err: any) => {
              console.error(`🔥 [FIRESTORE] ❌ update() ERROR:`, err);
              throw err;
            });
          },
          delete: () => {
            console.log(`🔥 [FIRESTORE] .delete()`);
            return firebaseModule.deleteDoc(docRef).then(() => {
              console.log(`🔥 [FIRESTORE] ✅ delete() SUCCESS`);
            }).catch((err: any) => {
              console.error(`🔥 [FIRESTORE] ❌ delete() ERROR:`, err);
              throw err;
            });
          },
          onSnapshot: (callback: any, error?: any) => {
            console.log(`🔥 [FIRESTORE] .onSnapshot() ATTACHED (single doc)`);
            return firebaseModule.onSnapshot(docRef, (snap: any) => {
              if (snap.exists()) {
                console.log(`🔥 [FIRESTORE] 📡 onSnapshot FIRED - doc exists`, snap.data());
              } else {
                console.log(`🔥 [FIRESTORE] 📡 onSnapshot FIRED - doc does not exist`);
              }
              callback(snap);
            }, (err: any) => {
              console.error(`🔥 [FIRESTORE] ❌ onSnapshot ERROR:`, err);
              error?.(err);
            });
          },
        };
      },
      batch: () => {
        console.log(`🔥 [FIRESTORE] 📦 .batch()`);
        const batch = firebaseModule.writeBatch(db);
        return {
          set: (docRef: any, data: any) => {
            console.log(`🔥 [FIRESTORE] batch.set()`, docRef.path, data);
            batch.set(docRef, data);
          },
          update: (docRef: any, data: any) => {
            console.log(`🔥 [FIRESTORE] batch.update()`, docRef.path, data);
            batch.update(docRef, data);
          },
          delete: (docRef: any) => {
            console.log(`🔥 [FIRESTORE] batch.delete()`, docRef.path);
            batch.delete(docRef);
          },
          commit: () => {
            console.log(`🔥 [FIRESTORE] batch.commit() - committing...`);
            return batch.commit().then(() => {
              console.log(`🔥 [FIRESTORE] ✅ batch.commit() SUCCESS`);
            }).catch((err: any) => {
              console.error(`🔥 [FIRESTORE] ❌ batch.commit() ERROR:`, err);
              throw err;
            });
          }
        };
      },
    };
    
    console.log('✅ [FIRESTORE] === REAL FIRESTORE WRAPPER CREATED ===');
    return cachedRealFirestore;
  } catch (error) {
    console.error('❌ [FIRESTORE] EXCEPTION:',  (error as any)?.message);
    console.error('❌ [FIRESTORE] Stack:', (error as any)?.stack);
    console.log('⚠️  [FIRESTORE] === FALLING BACK TO STUB ===');
    return firestoreStub;
  }
}
